import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import useSWR from 'swr';
import { t } from 'ttag';

import { Button, Heading, Text } from '@components';
import { Modal } from 'components';
import { getCookie } from '@utils';

import { useIsAuthenticated } from 'hooks';

import LoginModal from 'js/Account/Login/Modals/Modal';

import {
  AiLabel,
  MemberBenefits,
  PrizeDetails,
  PrizeDoors,
  PrizesHeader
} from './components';

import Badge from './assets/Badge.png';

import Hero1680 from './images/1680/010-engage1-dream-week.jpg';
import Hero1680Webp from './images/1680/010-engage1-dream-week.webp';
import Hero2160 from './images/2160/010-engage1-dream-week.jpg';
import Hero2160Webp from './images/2160/010-engage1-dream-week.webp';
import HeroLoadDesktop from './images/load/desktop/010-engage1-dream-week.jpg';
import HeroLoadMobile from './images/load/mobile/010-engage1-dream-week.jpg';
import HeroMobile from './images/mobile/010-engage1-dream-week.jpg';
import HeroMobileWebp from './images/mobile/010-engage1-dream-week.webp';

const HERO_DESKTOP_MEDIA = '(min-width: 1024px)';
const HERO_2160_MEDIA = '(min-width: 1681px)';
const PRIZES_SECTION_ID = 'dream-week-prizes';

/*
 * /!\ Important.
 * The Competitions React Application is currently only a single competition named "Dream Week".
 * It is planned to be extended to support multiple competitions in the future.
 */

const fetcher = url => fetch(url).then(r => r.json());

export default function Competitions() {
  const [showLogin, toggleLogin] = useState(false);
  const [showConfirmation, toggleConfirmation] = useState(false);
  const [showDetails, toggleDetails] = useState(false);
  // Kept after closing so the dialog keeps its content during the leave transition.
  const [detailsPrize, setDetailsPrize] = useState(null);
  const [isHeroLoaded, setIsHeroLoaded] = useState(false);
  const heroImageRef = useRef(null);

  // The image may already be complete (e.g. from cache) before onLoad is bound.
  useEffect(() => {
    if (heroImageRef.current?.complete) {
      setIsHeroLoaded(true);
    }
  }, []);

  const languageCode = useMemo(() => window?.config?.language_code || 'en', []);

  const termLinks = useMemo(
    () => ({
      en: 'https://www.unisportstore.com/stories/competition-rules/',
      da: 'https://www.unisport.dk/stories/konkurrencebetingelser/',
      sv: 'https://www.unisportstore.se/stories/tavlingsregler/',
      nb: 'https://www.unisportstore.no/stories/konkurranseregler/',
      fi: 'https://www.unisportstore.fi/stories/kilpailusaannot/',
      nl: 'https://www.unisportstore.nl/stories/wedstrijdregels/',
      de: 'https://www.unisportstore.de/stories/regeln-fur-wettbewerbe/',
      fr: 'https://www.unisportstore.fr/stories/regles-concours/',
      at: 'https://www.unisportstore.at/stories/regeln-fur-wettbewerbe/',
      pl: 'https://www.unisport.pl/stories/zasady-konkursu/',
      it: 'https://www.unisportstore.it/stories/regole-del-concorso/',
      el: 'https://www.unisport.gr/stories/kanones-diagonismou/',
      sl: 'https://www.unisportstore.si/stories/pravila-natecaja/',
      es: 'https://www.unisportstore.es/stories/reglas-del-concurso/',
      hr: 'https://www.unisportstore.hr/stories/pravila-nagradne-igre/'
    }),
    [languageCode]
  );

  const { data: prizesData, mutate } = useSWR(
    '/api/prizes/?online=True',
    fetcher
  );

  const prizes = useMemo(
    () =>
      [...(prizesData?.data ?? [])].sort(
        (a, b) =>
          new Date(a.attributes.valid_from) - new Date(b.attributes.valid_from)
      ),
    [prizesData]
  );

  const hasActivePrize = prizes.some(
    ({ attributes }) => attributes.status === 'Active'
  );

  const { isAuthenticated } = useIsAuthenticated();

  const handleParticipate = useCallback(
    async prizeId => {
      if (!isAuthenticated) {
        toggleLogin(true);
      } else {
        const response = await fetch(`/api/prizes/${prizeId}/participate/`, {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCookie('csrftoken')
          }
        });
        if (response.ok) {
          await mutate();
          toggleConfirmation(true);
        }
      }
    },
    [isAuthenticated]
  );

  const getButtonText = useCallback(attributes => {
    if (attributes?.status === 'Inactive' && !attributes?.is_participating) {
      return t`Expired`;
    }
    if (attributes?.is_participating) {
      return t`Joined`;
    }
    return t`Participate`;
  }, []);

  return (
    <main className="min-h-screen space-y-16 bg-whisper pb-32 md:space-y-32">
      {/* On mobile, fill the viewport below the Header (+ TinyBanner), matching the body offsets in skeleton.html. */}
      <section className="relative flex h-[672px] flex-col overflow-hidden bg-dark-gray max-lg:h-auto max-lg:min-h-[calc(100svh-64px)] max-lg:[.with-banner_&]:min-h-[calc(100svh-112px)]">
        <picture>
          <source media={HERO_DESKTOP_MEDIA} srcSet={HeroLoadDesktop} />
          <img
            src={HeroLoadMobile}
            alt=""
            className="absolute inset-0 h-full w-full scale-110 object-cover object-center blur-xl"
          />
        </picture>
        {/* WebP is about 60% smaller; the JPEGs are the fallback for the few browsers
            without it, such as Safari 14 before macOS 11. */}
        <picture>
          <source
            media={HERO_2160_MEDIA}
            type="image/webp"
            srcSet={Hero2160Webp}
          />
          <source media={HERO_2160_MEDIA} srcSet={Hero2160} />
          <source
            media={HERO_DESKTOP_MEDIA}
            type="image/webp"
            srcSet={Hero1680Webp}
          />
          <source media={HERO_DESKTOP_MEDIA} srcSet={Hero1680} />
          <source type="image/webp" srcSet={HeroMobileWebp} />
          <img
            ref={heroImageRef}
            src={HeroMobile}
            alt=""
            onLoad={() => setIsHeroLoaded(true)}
            className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-500 ${
              isHeroLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </picture>
        {/* The top padding clears the badge and AI label when the copy fills a short screen. */}
        <div className="container relative z-10 flex flex-1 flex-col justify-end pb-12 pt-20 md:pb-20 md:pt-28">
          <img
            src={Badge}
            alt=""
            className="absolute right-4 top-4 h-12 w-12 md:h-20 md:w-20"
          />
          {/* Top left on phones, where the chat bubble can't cover it and the badge
              holds the top right. */}
          <AiLabel
            compact="mobile"
            className="left-4 right-auto top-4 md:bottom-4 md:left-auto md:right-4 md:top-auto"
          />
          <div className="max-w-screen-md space-y-4 md:space-y-8">
            {/* The phone size scales with the screen, so the longest translated word
                ("Traumwoche") fits on one line down to a 340px wide screen. */}
            <Heading
              as="h1"
              className="hyphens-auto break-words text-[12.5vw] leading-[0.9] tracking-[-0.02em] text-white md:text-[96px] xl:text-[104px]"
            >
              {t`Dream Week`}
            </Heading>
            <div className="space-y-2 md:space-y-4">
              <Text className="text-lg font-semibold leading-snug text-white md:text-2xl">
                {t`Football dreams come true during Dream Week, exclusively for Unisport FC members.`}
              </Text>
              <Text className="leading-relaxed text-white">
                {isAuthenticated
                  ? t`A new prize unlocks every day, from unforgettable football trips and exclusive experiences to signed football shirts and boot deals. As a member, you're ready to enter today's competition below.`
                  : t`A new prize unlocks every day, from unforgettable football trips and exclusive experiences to signed football shirts and boot deals. Membership is free, so join Unisport FC and start entering.`}
              </Text>
            </div>
            {isAuthenticated ? (
              <Button as="a" size="md" href={`#${PRIZES_SECTION_ID}`}>
                <Button.Text>
                  {hasActivePrize ? t`See today's prize` : t`See the prizes`}
                </Button.Text>
              </Button>
            ) : (
              <Button size="md" onClick={() => toggleLogin(true)}>
                <Button.Text>{t`Join Unisport FC`}</Button.Text>
              </Button>
            )}
          </div>
        </div>
      </section>
      {/* scroll-mt clears the fixed Header (+ TinyBanner) when jumping to the prizes. */}
      <section
        id={PRIZES_SECTION_ID}
        className="container scroll-mt-32 space-y-8 md:space-y-16 lg:scroll-mt-44"
      >
        <PrizesHeader
          prizes={prizes}
          termsLink={termLinks[languageCode]}
          onCountdownElapsed={mutate}
        />
        <PrizeDoors
          prizes={prizes}
          getButtonText={getButtonText}
          onParticipate={handleParticipate}
          onDetails={prize => {
            setDetailsPrize(prize);
            toggleDetails(true);
          }}
        />
      </section>
      <MemberBenefits
        isAuthenticated={isAuthenticated}
        prizesHref={`#${PRIZES_SECTION_ID}`}
        onJoin={() => toggleLogin(true)}
      />
      <PrizeDetails
        isOpen={showDetails}
        prize={detailsPrize}
        termsLink={termLinks[languageCode]}
        getButtonText={getButtonText}
        onClose={() => toggleDetails(false)}
        onParticipate={prizeId => {
          toggleDetails(false);
          handleParticipate(prizeId);
        }}
      />
      <LoginModal
        isOpen={showLogin}
        onClose={() => {
          toggleLogin(false);
        }}
        modal="login"
        loginHeading={t`Join Unisport FC to Participate in Dream Week`}
      />
      <Modal
        isOpen={showConfirmation}
        onClose={() => {
          toggleConfirmation(false);
        }}
        title={t`Thank you for participating!`}
        maxWidth="md"
      >
        <div className="space-y-2">
          <Text>
            {t`Thank you for being a Unisport FC member! You are now participating in the competition. The winner will be randomly picked from all entries and contacted by e-mail by 20.10.2026.`}
          </Text>
          <a
            href={termLinks[languageCode]}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-block text-black underline"
          >{t`Read Unisport's Competition Terms`}</a>
        </div>
      </Modal>
    </main>
  );
}
